<script setup lang="ts">
import {computed} from "vue";
import {Sparkles} from "@lucide/vue";
import {storeToRefs} from "pinia";
import {Button} from "@/core/components/ui/button";
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "@/core/components/ui/tooltip";
import {useAiAssistantStore} from "@/stores/aiAssistant.store";
import {PLANNING_ASSISTANT} from "@/ai-assistant/constants";

/**
 * Lives in the screen it serves rather than in the application bar: the
 * assistant answers on the team planning, so the button belongs to the
 * calendar's action row and follows its idiom — label dropped when the row
 * gets cramped.
 *
 * It carries its own tooltip provider, like `BaseTooltip` and
 * `InformationTooltip` do. Relying on the host to supply one made the button
 * undroppable: the agenda toolbar happened to have one, and the two screens
 * that did not crashed on an injection the component never asked for out loud.
 */
const {labelsHidden = false, compact = false} = defineProps<{
    labelsHidden?: boolean;
    /** For a toolbar built on 32px controls rather than on the default 36. */
    compact?: boolean;
}>();

const store = useAiAssistantStore();
const {isOpen, minimized} = storeToRefs(store);

/**
 * A toggle, so it says whether the window is in view: filled in the gradient
 * of the window's own header, so the two read as one thing, and ringed while
 * the window shows. A folded-down window does not count as shown — the click
 * brings it back.
 */
const shown = computed<boolean>(() => isOpen.value && !minimized.value);
</script>

<template>
  <TooltipProvider>
    <Tooltip :delay-duration="350">
      <TooltipTrigger as-child>
        <Button
          :size="labelsHidden ? (compact ? 'icon-sm' : 'icon') : (compact ? 'sm' : 'default')"
          class="bg-gradient-to-r from-primary-dark to-primary text-white hover:opacity-90"
          :class="shown ? 'ring-2 ring-primary ring-offset-2' : ''"
          :aria-label="shown ? 'Masquer l\'assistant planning d\'équipe' : 'Demander à l\'assistant planning d\'équipe'"
          :aria-pressed="shown"
          @click="store.toggle(PLANNING_ASSISTANT)"
        >
          <Sparkles class="size-4" />
          <span v-if="!labelsHidden">Demander à l'IA</span>
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom" class="text-xs">
        <p>{{ shown ? "Masquer l'assistant" : 'Poser une question sur le planning de votre équipe' }}</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
</template>
